import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import ResearchLayout from '../components/ResearchLayout';
import Modal from '../components/Modal';
import '../assets/css/HelpPage.css';

const HelpPage = () => {
  const { user } = useAuth();
  const [expandedSection, setExpandedSection] = useState(null);
  const [showSupportModal, setShowSupportModal] = useState(false);

  const toggleSection = (section) => {
    setExpandedSection(expandedSection === section ? null : section);
  };

  const handleEmailSupport = () => {
    setShowSupportModal(true);
  };

  const handleSendEmail = () => {
    const subject = encodeURIComponent('HealthIQ Support - Access Issue');
    const body = encodeURIComponent(
      `Hello Support Team,\n\nI am experiencing issues accessing the HealthIQ platform.\n\nPlease provide details about:\n- What issue you're experiencing\n- Which device/browser you're using\n- Steps you've already taken to troubleshoot\n\nThank you,\n${user?.name || 'User'}`
    );
    window.location.href = `mailto:support@hlthiq.com?subject=${subject}&body=${body}`;
    setShowSupportModal(false);
  };

  const sections = [
    {
      id: 'troubleshooting',
      title: 'Common Troubleshooting Steps',
      icon: 'fas fa-wrench',
      content: (
        <div className="help-section-content">
          <p className="help-intro">
            If you're having trouble accessing the HealthIQ platform, follow these steps to resolve most common issues.
          </p>
          
          <div className="troubleshooting-steps">
            <div className="step">
              <div className="step-number">1</div>
              <div className="step-content">
                <h4>Clear Your Browser Cache & Cookies</h4>
                <p>
                  Outdated cache or corrupted cookies can prevent proper page loading. Here's how to clear them:
                </p>
                <ul>
                  <li><strong>Chrome:</strong> Press Ctrl+Shift+Delete (Windows) or Cmd+Shift+Delete (Mac) → Select "All time" → Check "Cookies and other site data" and "Cached images and files" → Click "Clear data"</li>
                  <li><strong>Firefox:</strong> Press Ctrl+Shift+Delete (Windows) or Cmd+Shift+Delete (Mac) → Select "Everything" → Check the boxes for cookies and cache → Click "Clear Now"</li>
                  <li><strong>Safari:</strong> Click Safari menu → Preferences → Privacy tab → Click "Manage Website Data" → Select HealthIQ entries → Click "Remove"</li>
                  <li><strong>Edge:</strong> Press Ctrl+Shift+Delete → Select "All time" → Check "Cookies and other site data" and "Cached images and files" → Click "Clear now"</li>
                </ul>
              </div>
            </div>

            <div className="step">
              <div className="step-number">2</div>
              <div className="step-content">
                <h4>Try a Different Browser</h4>
                <p>
                  Browser compatibility issues can sometimes cause problems. Try accessing HealthIQ Research Platform with:
                </p>
                <ul>
                  <li>Google Chrome (Recommended)</li>
                  <li>Mozilla Firefox</li>
                  <li>Microsoft Edge</li>
                  <li>Safari (for Mac users)</li>
                </ul>
                <p className="note"><i className="fas fa-info-circle"></i> We recommend using Chrome for the best experience.</p>
              </div>
            </div>

            <div className="step">
              <div className="step-number">3</div>
              <div className="step-content">
                <h4>Refresh the Page</h4>
                <p>
                  Sometimes a simple page refresh resolves temporary issues:
                </p>
                <ul>
                  <li>Press <strong>F5</strong> (Windows) or <strong>Cmd+R</strong> (Mac)</li>
                  <li>For a hard refresh (ignoring cache): <strong>Ctrl+F5</strong> (Windows) or <strong>Cmd+Shift+R</strong> (Mac)</li>
                </ul>
              </div>
            </div>

            <div className="step">
              <div className="step-number">4</div>
              <div className="step-content">
                <h4>Check Your Internet Connection</h4>
                <p>
                  A weak or unstable internet connection can cause access problems. Try:
                </p>
                <ul>
                  <li>Restarting your WiFi router</li>
                  <li>Moving closer to your WiFi router</li>
                  <li>Switching to a wired connection (Ethernet cable)</li>
                  <li>Testing on a different network (mobile hotspot, different WiFi)</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      )
    },
    {
      id: 'screenshots',
      title: 'How to Take a Screenshot',
      icon: 'fas fa-camera',
      content: (
        <div className="help-section-content">
          <p className="help-intro">
            If the issue persists, taking a screenshot of the error or problem can help our support team diagnose the issue faster.
          </p>
          
          <div className="device-guides">
            <div className="device-guide">
              <div className="device-header">
                <i className="fas fa-laptop"></i>
                <h4>Windows Laptop or Desktop</h4>
              </div>
              <div className="device-instructions">
                <div className="instruction">
                  <span className="method-label">Method 1: Print Screen</span>
                  <ol>
                    <li>Press the <strong>Print Screen (PrtScn)</strong> key on your keyboard</li>
                    <li>Open Paint, Word, or any image editor</li>
                    <li>Press <strong>Ctrl+V</strong> to paste the screenshot</li>
                    <li>Save the file as an image</li>
                  </ol>
                </div>
                <div className="instruction">
                  <span className="method-label">Method 2: Snipping Tool (Recommended)</span>
                  <ol>
                    <li>Press <strong>Windows Key + Shift + S</strong></li>
                    <li>Select the area of the screen you want to capture</li>
                    <li>The screenshot will be copied to your clipboard</li>
                    <li>Paste it in an email or document</li>
                  </ol>
                </div>
              </div>
            </div>

            <div className="device-guide">
              <div className="device-header">
                <i className="fas fa-apple"></i>
                <h4>Mac Laptop</h4>
              </div>
              <div className="device-instructions">
                <div className="instruction">
                  <span className="method-label">Full Screen Screenshot</span>
                  <ol>
                    <li>Press <strong>Cmd+Shift+3</strong></li>
                    <li>The screenshot will be saved to your desktop</li>
                  </ol>
                </div>
                <div className="instruction">
                  <span className="method-label">Partial Screenshot (Recommended)</span>
                  <ol>
                    <li>Press <strong>Cmd+Shift+4</strong></li>
                    <li>Drag to select the area you want to capture</li>
                    <li>The screenshot will be saved to your desktop</li>
                  </ol>
                </div>
                <div className="instruction">
                  <span className="method-label">Copy to Clipboard</span>
                  <ol>
                    <li>Press <strong>Cmd+Shift+4</strong>, then press <strong>Spacebar</strong></li>
                    <li>Click on the window you want to capture</li>
                    <li>Press <strong>Cmd+Control+Shift+3</strong> or <strong>Cmd+Control+Shift+4</strong></li>
                    <li>Paste with <strong>Cmd+V</strong></li>
                  </ol>
                </div>
              </div>
            </div>

            <div className="device-guide">
              <div className="device-header">
                <i className="fas fa-android"></i>
                <h4>Android Phone or Tablet</h4>
              </div>
              <div className="device-instructions">
                <div className="instruction">
                  <span className="method-label">Most Android Devices</span>
                  <ol>
                    <li>Press and hold the <strong>Power button</strong> and <strong>Volume Down button</strong> simultaneously for 2-3 seconds</li>
                    <li>You should see a confirmation or animation</li>
                    <li>The screenshot will be saved to your device's Photos or Gallery app</li>
                  </ol>
                </div>
                <div className="instruction">
                  <span className="method-label">Alternative Method</span>
                  <ol>
                    <li>Open the page with the issue</li>
                    <li>Try: Power button + Volume Up</li>
                    <li>If that doesn't work, check your device's Settings &gt; System &gt; Gestures for screenshot options</li>
                  </ol>
                </div>
                <div className="instruction">
                  <span className="note-box">
                    <i className="fas fa-lightbulb"></i> <strong>Tip:</strong> You can find screenshots in your Photos/Gallery app or in a "Screenshots" folder in your device storage.
                  </span>
                </div>
              </div>
            </div>

            <div className="device-guide">
              <div className="device-header">
                <i className="fas fa-apple"></i>
                <h4>iPhone or iPad (iOS)</h4>
              </div>
              <div className="device-instructions">
                <div className="instruction">
                  <span className="method-label">iPhone X and Later (No Home Button)</span>
                  <ol>
                    <li>Press the <strong>Side button</strong> and <strong>Volume Up button</strong> simultaneously</li>
                    <li>Release both buttons quickly</li>
                    <li>You'll see a thumbnail appear briefly in the bottom-left corner</li>
                    <li>The screenshot is automatically saved to your Photos app</li>
                  </ol>
                </div>
                <div className="instruction">
                  <span className="method-label">iPhone 8 and Earlier (With Home Button)</span>
                  <ol>
                    <li>Press the <strong>Top (or Side) button</strong> and <strong>Home button</strong> simultaneously</li>
                    <li>Release both buttons quickly</li>
                    <li>The screenshot will be saved to your Photos app</li>
                  </ol>
                </div>
                <div className="instruction">
                  <span className="method-label">iPad</span>
                  <ol>
                    <li>Press the <strong>Top (or Side) button</strong> and <strong>Volume Up button</strong> simultaneously</li>
                    <li>Release quickly</li>
                    <li>Screenshots are saved to your Photos app</li>
                  </ol>
                </div>
                <div className="instruction">
                  <span className="note-box">
                    <i className="fas fa-lightbulb"></i> <strong>Tip:</strong> Screenshots are saved in your Photos app under the "Screenshots" album.
                  </span>
                </div>
              </div>
            </div>
          </div>

          <div className="screenshot-tips">
            <h5><i className="fas fa-star"></i> Tips for Better Screenshots</h5>
            <ul>
              <li>Include the full error message or the problematic area</li>
              <li>Make sure the URL bar is visible (helps identify the page)</li>
              <li>For browser errors, include the browser console errors if possible</li>
              <li>Multiple screenshots showing different stages of the issue are helpful</li>
            </ul>
          </div>
        </div>
      )
    },
    {
      id: 'contact-support',
      title: 'Still Having Issues?',
      icon: 'fas fa-life-ring',
      content: (
        <div className="help-section-content contact-support-section">
          <p className="help-intro">
            If you've tried all the troubleshooting steps above and are still experiencing issues, our support team is here to help!
          </p>
          
          <div className="contact-info-box">
            <h4>Get Support</h4>
            <p>
              Please email our support team with:
            </p>
            <ul className="contact-checklist">
              <li><i className="fas fa-check-circle"></i> A description of the issue you're experiencing</li>
              <li><i className="fas fa-check-circle"></i> Which device/browser you're using (e.g., Chrome on Windows)</li>
              <li><i className="fas fa-check-circle"></i> Steps you've already tried</li>
              <li><i className="fas fa-check-circle"></i> Screenshot(s) of the error or issue</li>
            </ul>
          </div>

          <button className="email-support-btn" onClick={handleEmailSupport}>
            <i className="fas fa-envelope"></i>
            <span>Send Email to Support</span>
          </button>

          <p className="support-email-info">
            <strong>Email:</strong> support@hlthiq.com
          </p>

          <div className="response-time-info">
            <i className="fas fa-clock"></i>
            <p>We typically respond to support requests within <strong>24 business hours</strong>.</p>
          </div>
        </div>
      )
    },
    {
      id: 'ticketing-process',
      title: 'Support Ticketing Process',
      icon: 'fas fa-ticket-alt',
      content: (
        <div className="help-section-content">
          <p className="help-intro">
            When you send an email to our support team, a support ticket is automatically created. Here's how the process works:
          </p>

          <div className="ticketing-timeline">
            <div className="timeline-item">
              <div className="timeline-marker">
                <i className="fas fa-envelope"></i>
              </div>
              <div className="timeline-content">
                <h4>1. Submit Your Request</h4>
                <p>
                  You email support@hlthiq.com with details about your issue. A support ticket is immediately created and assigned a unique ticket ID.
                </p>
                <div className="timeline-note">
                  <i className="fas fa-info-circle"></i>
                  Make sure to include all relevant details in your initial email to help us resolve your issue faster.
                </div>
              </div>
            </div>

            <div className="timeline-item">
              <div className="timeline-marker">
                <i className="fas fa-hourglass-start"></i>
              </div>
              <div className="timeline-content">
                <h4>2. Initial Response (24 Hours)</h4>
                <p>
                  Our support team reviews your ticket and sends you an initial response within <strong>24 business hours</strong>. This response may include:
                </p>
                <ul className="response-items">
                  <li>Acknowledgment of your issue</li>
                  <li>Confirmation of your ticket ID for future reference</li>
                  <li>Initial troubleshooting steps or questions</li>
                  <li>Estimated timeline for resolution</li>
                </ul>
              </div>
            </div>

            <div className="timeline-item">
              <div className="timeline-marker">
                <i className="fas fa-envelope-open"></i>
              </div>
              <div className="timeline-content">
                <h4>3. Monitor Your Email</h4>
                <p>
                  <strong>Important:</strong> Check your email regularly (including spam/junk folders) for responses from our support team. 
                </p>
                <div className="email-monitoring-tips">
                  <h5><i className="fas fa-star"></i> Email Monitoring Tips:</h5>
                  <ul>
                    <li><strong>Check daily:</strong> Look for updates from support@hlthiq.com at least once per day</li>
                    <li><strong>Check spam folder:</strong> Sometimes support emails may be filtered as spam - add support@hlthiq.com to your contacts</li>
                    <li><strong>Enable notifications:</strong> Turn on email notifications on your device so you don't miss responses</li>
                    <li><strong>Save ticket ID:</strong> Keep the ticket ID in a safe place for future reference</li>
                    <li><strong>Keep the conversation in one thread:</strong> Reply to support emails directly rather than sending new emails</li>
                  </ul>
                </div>
              </div>
            </div>

            <div className="timeline-item">
              <div className="timeline-marker">
                <i className="fas fa-comments"></i>
              </div>
              <div className="timeline-content">
                <h4>4. Ongoing Communication</h4>
                <p>
                  Our support team may request additional information or screenshots to better understand your issue. Please respond promptly to keep your ticket moving forward.
                </p>
                <div className="communication-guidelines">
                  <h5><i className="fas fa-comments-dollar"></i> Communication Guidelines:</h5>
                  <ul>
                    <li>Reply to emails within 48 hours to avoid delays</li>
                    <li>Provide clear and detailed responses to questions</li>
                    <li>Share screenshots or additional information when requested</li>
                    <li>Let us know if the issue has changed or new details emerge</li>
                  </ul>
                </div>
              </div>
            </div>

            <div className="timeline-item">
              <div className="timeline-marker">
                <i className="fas fa-check-circle"></i>
              </div>
              <div className="timeline-content">
                <h4>5. Issue Resolution</h4>
                <p>
                  Once our support team has resolved your issue, they will send you a resolution email with:
                </p>
                <ul className="response-items">
                  <li>Explanation of the issue and what caused it</li>
                  <li>Steps taken to resolve it</li>
                  <li>How to prevent the issue in the future (if applicable)</li>
                  <li>Next steps or recommendations</li>
                </ul>
              </div>
            </div>

            <div className="timeline-item">
              <div className="timeline-marker">
                <i className="fas fa-lock"></i>
              </div>
              <div className="timeline-content">
                <h4>6. Ticket Closure</h4>
                <p>
                  After your issue has been resolved, the support team will close your ticket. You will receive a final email notification confirming the closure.
                </p>
                <div className="closure-info">
                  <h5><i className="fas fa-info-circle"></i> After Closure:</h5>
                  <ul>
                    <li><strong>Reopening:</strong> If the issue resurfaces, you can reply to the closed ticket email, and it will be automatically reopened</li>
                    <li><strong>Reference:</strong> Keep the ticket ID in case you need to reference this support case later</li>
                    <li><strong>Feedback:</strong> We may request feedback on your support experience</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>

          <div className="support-sla-box">
            <h5><i className="fas fa-star"></i> Support SLA (Service Level Agreement)</h5>
            <div className="sla-grid">
              <div className="sla-item">
                <div className="sla-icon">
                  <i className="fas fa-clock"></i>
                </div>
                <div className="sla-text">
                  <strong>Initial Response</strong>
                  <p>24 business hours</p>
                </div>
              </div>
              <div className="sla-item">
                <div className="sla-icon">
                  <i className="fas fa-sync"></i>
                </div>
                <div className="sla-text">
                  <strong>Follow-up Updates</strong>
                  <p>Every 48 hours</p>
                </div>
              </div>
              <div className="sla-item">
                <div className="sla-icon">
                  <i className="fas fa-hourglass-end"></i>
                </div>
                <div className="sla-text">
                  <strong>Resolution Target</strong>
                  <p>5-7 business days</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )
    }
  ];

  return (
    <ResearchLayout pageTitle="Help & Support">
      <div className="help-page">
        <div className="help-header">
          <h1><i className="fas fa-question-circle"></i> Help & Support Center</h1>
          <p>Find solutions to common issues and learn how to get the best experience with HealthIQ</p>
        </div>

        <div className="help-container">
          <div className="help-sections">
            {sections.map((section) => (
              <div key={section.id} className="help-section-card">
                <button
                  className={`section-header ${expandedSection === section.id ? 'expanded' : ''}`}
                  onClick={() => toggleSection(section.id)}
                >
                  <div className="section-title-wrapper">
                    <i className={section.icon}></i>
                    <h2>{section.title}</h2>
                  </div>
                  <i className={`fas fa-chevron-${expandedSection === section.id ? 'up' : 'down'}`}></i>
                </button>

                {expandedSection === section.id && (
                  <div className="section-body">
                    {section.content}
                  </div>
                )}
              </div>
            ))}
          </div>

          <div className="help-sidebar">
            <div className="quick-help-card">
              <h3><i className="fas fa-bolt"></i> Quick Tips</h3>
              <ul>
                <li>Clear cache regularly for optimal performance</li>
                <li>Use Chrome for the best experience</li>
                <li>Ensure your internet connection is stable</li>
                <li>Keep your browser updated</li>
                <li>Disable browser extensions if issues persist</li>
              </ul>
            </div>

            <div className="faq-card">
              <h3><i className="fas fa-book"></i> FAQ</h3>
              <div className="faq-item">
                <strong>Q: I'm seeing a blank page</strong>
                <p>A: Try clearing your cache and cookies, then refresh the page. If it persists, try a different browser.</p>
              </div>
              <div className="faq-item">
                <strong>Q: The page loads slowly</strong>
                <p>A: Check your internet connection. Clear cache and try disabling browser extensions.</p>
              </div>
              <div className="faq-item">
                <strong>Q: I'm getting login errors</strong>
                <p>A: Verify your credentials are correct. Clear cookies and try logging in again. Contact support if the issue persists.</p>
              </div>
              <div className="faq-item">
                <strong>Q: My data isn't loading</strong>
                <p>A: Refresh the page and wait a few moments. If the issue continues, contact our support team.</p>
              </div>
            </div>

            <div className="contact-card">
              <h3><i className="fas fa-envelope"></i> Contact Support</h3>
              <p>Email us anytime for assistance:</p>
              <button 
                onClick={() => setShowSupportModal(true)}
                className="support-email-link"
                style={{ cursor: 'pointer', border: 'none', background: 'none', padding: 0 }}
              >
                <i className="fas fa-envelope-open"></i>
                <p>Send an Email </p>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Support Modal */}
      <Modal
        isOpen={showSupportModal}
        onClose={() => setShowSupportModal(false)}
        title="Contact Support"
        size="md"
        confirmText="Send Email"
        onConfirm={handleSendEmail}
      >
        <div className="support-modal-content">
          <div className="support-modal-header">
            <i className="fas fa-envelope-circle-check"></i>
            <h5>Get in Touch with Our Support Team</h5>
          </div>
          
          <div className="support-modal-body">
            <p>
              We're here to help! Click the button below to open your email client and send us a message.
            </p>
            
            <div className="support-info-box">
              <div className="info-item">
                <i className="fas fa-envelope"></i>
                <div>
                  <strong>Email Address</strong>
                  <p>support@hlthiq.com</p>
                </div>
              </div>
              
              <div className="info-item">
                <i className="fas fa-clock"></i>
                <div>
                  <strong>Response Time</strong>
                  <p>Within 24 business hours</p>
                </div>
              </div>
              
              <div className="info-item">
                <i className="fas fa-user"></i>
                <div>
                  <strong>Your Information</strong>
                  <p>{user?.name || 'User'}</p>
                </div>
              </div>
            </div>
            
            <div className="support-tips">
              <h6><i className="fas fa-lightbulb"></i> Tips for Your Email:</h6>
              <ul>
                <li>Describe the issue you're experiencing in detail</li>
                <li>Include which device and browser you're using</li>
                <li>Mention the troubleshooting steps you've already tried</li>
                <li>Attach screenshots if possible</li>
              </ul>
            </div>
          </div>
        </div>
      </Modal>
    </ResearchLayout>
  );
};

export default HelpPage;
